import React, { useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Row, Col } from 'reactstrap';
import { Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import { RangeValue } from 'rc-picker/lib/interface';

import { getPeriode, updatePeriode, createPeriode, reset } from './periode-evaluation.reducer';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getCycles } from 'app/entities/cycle/cycle.reducer';
import { getAnneeScolaire, getEcoleActiveAnneeScolaire } from 'app/entities/annee-scolaire/annee-scolaire.reducer';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';

export const PeriodeEvaluationUpdate = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId?: string; id: string }>) => {
  const dispatch = useAppDispatch();
  const [isNew] = useState(!props.match.params || !props.match.params.id);
  const [dateRange, setDateRange] = useState<[Moment | null, Moment | null] | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  const periodeEntity = useAppSelector(state => state.periodeEvaluation.entity);
  const loading = useAppSelector(state => state.periodeEvaluation.loading);
  const updating = useAppSelector(state => state.periodeEvaluation.updating);
  const updateSuccess = useAppSelector(state => state.periodeEvaluation.updateSuccess);
  const cycles = useAppSelector(state => state.cycle.entities);
  const anneeScolaire = useAppSelector(state => state.anneeScolaire.entityActive);

  const { ecoleid, anneeScolaireId, id } = props.match.params;
  const isClosed = !isNew && periodeEntity.statut === StatutPeriodeEvaluation.FERMEE;

  const handleClose = () => {
    props.history.push(anneeScolaireId ? `/periode-evaluation/${ecoleid}/${anneeScolaireId}` : `/periode-evaluation/${ecoleid}/`);
  };

  useEffect(() => {
    dispatch(getCycles(ecoleid));
    if (anneeScolaireId) {
      dispatch(getAnneeScolaire(anneeScolaireId));
    } else {
      dispatch(getEcoleActiveAnneeScolaire(ecoleid));
    }
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getPeriode(id));
    }
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (isNew) {
      setDateRange(null);
      return;
    }

    setDateRange([
      periodeEntity.dateDebut ? moment(periodeEntity.dateDebut) : null,
      periodeEntity.dateFin ? moment(periodeEntity.dateFin) : null,
    ]);
  }, [isNew, periodeEntity.dateDebut, periodeEntity.dateFin]);

  const handleDateRangeChange = (dates: RangeValue<Moment>) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange(null);
      setDateRangeError(null);
      return;
    }

    setDateRange([dates[0], dates[1]]);
    setDateRangeError(null);
  };

  const disabledDate = (current: Moment) => {
    const yearStart = anneeScolaire?.dateDebut ? moment(anneeScolaire.dateDebut) : null;
    const yearEnd = anneeScolaire?.dateCloture ? moment(anneeScolaire.dateCloture) : null;

    if (yearStart && current.isBefore(yearStart, 'day')) {
      return true;
    }

    if (yearEnd && current.isAfter(yearEnd, 'day')) {
      return true;
    }

    return false;
  };

  const saveEntity = values => {
    if (isClosed) {
      return;
    }
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      setDateRangeError('Veuillez sélectionner une plage de dates.');
      return;
    }

    const entity = {
      ...periodeEntity,
      ...values,
      dateDebut: dateRange[0].format('YYYY-MM-DD'),
      dateFin: dateRange[1].format('YYYY-MM-DD'),
      ecoleId: ecoleid,
      anneeScolaireId: anneeScolaireId || anneeScolaire?.id,
    };
    if (isNew) {
      dispatch(createPeriode(entity));
    } else {
      dispatch(updatePeriode(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          ...periodeEntity,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="korischoolApp.periodeEvaluation.home.createOrEditLabel" data-cy="PeriodeCreateUpdateHeading">
            <Translate contentKey="korischoolApp.periodeEvaluation.home.createOrEditLabel">Create or edit a Period</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {isClosed && (
                <Alert
                  type="warning"
                  showIcon
                  message="Période fermée"
                  description="Une période fermée ne peut pas être modifiée. Rouvrez-la avant toute modification."
                  style={{ marginBottom: 16 }}
                />
              )}
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="periode-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('korischoolApp.periodeEvaluation.cycle')}
                id="periode-cycleId"
                name="cycleId"
                data-cy="cycleId"
                type="select"
                disabled={isClosed}
              >
                <option value="" key="0" />
                {cycles.map(cycle => (
                  <option value={cycle.id} key={cycle.id}>
                    {cycle.nom}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('korischoolApp.periodeEvaluation.nom')}
                id="periode-nom"
                name="nom"
                data-cy="nom"
                type="text"
                disabled={isClosed}
              />
              <div className="form-group">
                <label htmlFor="periode-date-range">Date</label>
                <DatePicker.RangePicker
                  id="periode-date-range"
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  placeholder={['Date de début', 'Date de fin']}
                  separator="au"
                  allowClear
                  disabledDate={disabledDate}
                  disabled={isClosed}
                />
                {dateRangeError ? (
                  <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{dateRangeError}</div>
                ) : null}
              </div>
              <Button onClick={() => props.history.goBack()} id="cancel-save" data-cy="entityCreateCancelButton" color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button
                style={{ backgroundColor: '#F28E19', color: 'white' }}
                id="save-entity"
                data-cy="entityCreateSaveButton"
                type="submit"
                disabled={updating || isClosed}
              >
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PeriodeEvaluationUpdate;
